import {
  clearAnalyticsSession,
  enrichEventProps,
  setAnalyticsSession,
} from "./context";
import * as fullstory from "./fullstory";
import * as mixpanel from "./mixpanel";
import * as posthog from "./posthog";
import type { AnalyticsProps } from "./props";
import { MERCHANT_EVENT_TAXONOMY } from "./taxonomy";

export type AnalyticsConfig = {
  fullstoryOrgId?: string;
  posthogKey?: string;
  posthogHost?: string;
  mixpanelToken?: string;
};

/** Init every configured destination. Missing keys are skipped. */
export function initAnalytics(config: AnalyticsConfig) {
  fullstory.initFullStory({ orgId: config.fullstoryOrgId });
  posthog.initPostHog({
    apiKey: config.posthogKey,
    apiHost: config.posthogHost,
  });
  mixpanel.initMixpanel({ token: config.mixpanelToken });
}

function withMerchantContext(
  event: string,
  properties?: AnalyticsProps
): AnalyticsProps {
  return enrichEventProps(event, properties, {
    actor: "user",
    surface: "merchant_dashboard",
    taxonomyByEvent: MERCHANT_EVENT_TAXONOMY,
  });
}

export function track(event: string, properties?: AnalyticsProps) {
  const props = withMerchantContext(event, properties);
  fullstory.track(event, props);
  posthog.track(event, props);
  mixpanel.track(event, props);
}

export function identify(userId: string, traits?: AnalyticsProps) {
  fullstory.identify(userId, traits);
  posthog.identify(userId, traits);
  mixpanel.identify(userId, traits);

  const organizationId = traits?.organization_id;
  if (organizationId !== undefined && organizationId !== null) {
    groupMerchant(String(organizationId), {
      name: traits?.organization_name,
    });
    setAnalyticsSession({
      merchant_id: String(organizationId),
      organization_id: String(organizationId),
      organization_name: traits?.organization_name,
      user_id: traits?.user_id ?? userId,
      email: traits?.email,
    });
  }
}

/** PostHog/Mixpanel group analytics — merchant = organization. */
export function groupMerchant(merchantId: string, traits?: AnalyticsProps) {
  if (!merchantId) return;
  posthog.group("merchant", merchantId, traits);
  mixpanel.group("merchant", merchantId, traits);
  setAnalyticsSession({
    merchant_id: merchantId,
    organization_id: merchantId,
    ...traits,
  });
}

export function page(name?: string, properties?: AnalyticsProps) {
  const props = enrichEventProps(
    name || "Page View",
    { taxonomy_id: "M2", ...properties },
    {
      actor: "user",
      surface: "merchant_dashboard",
      taxonomyByEvent: MERCHANT_EVENT_TAXONOMY,
    }
  );
  fullstory.page(name, props);
  posthog.page(name, props);
  mixpanel.page(name, props);
}

export function reset() {
  clearAnalyticsSession();
  fullstory.reset();
  posthog.reset();
  mixpanel.reset();
}

export function trackMerchant(event: string, properties?: AnalyticsProps) {
  track(event, properties);
}
